import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';

import { AiSummaryStoreService } from '../../../services/stores/ai-summary-store.service';
import { PropertyStoreService } from '../../../services/stores/property-store.service';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';

import { AiDaySummary } from '../../../domain/Ai/ai-day-summary';

@Component({
   selector: 'app-daily-task-summary',
   standalone: true,
   imports: [CommonModule, CardModule, ButtonModule],
   templateUrl: './daily-task-summary.component.html',
   styleUrls: ['./daily-task-summary.component.scss'],
   providers: [DatePropertyTimezonePipe]
})
export class DailyTaskSummaryComponent implements OnDestroy {
   summary: AiDaySummary | null = null;
   totalTaskNumber = 0;
   loading = true;
   errorStatus: number | null = null;

   private destroy$ = new Subject<void>();

   constructor(
      private aiSummaryStore: AiSummaryStoreService,
      private propertyStore: PropertyStoreService,
      private router: Router,
      private datePipe: DatePropertyTimezonePipe
   ) {
      this.propertyStore.currentProperty
         .pipe(takeUntil(this.destroy$))
         .subscribe((property) => {
            if (!property?.id) {
               this.resetState();
               return;
            }

            const timezone = this.datePipe.currentPropertyTimezone;
            const yesterday = DateTime.now()
               .setZone(timezone)
               .minus({ days: 1 })
               .toISODate();

            this.loading = true;

            this.aiSummaryStore.summary$
               .pipe(takeUntil(this.destroy$))
               .subscribe((summary) => {
                  if (!summary) {
                     this.resetState();
                     return;
                  }

                  this.totalTaskNumber = summary.totalTasksInstanceLaunched ?? 0;

                  this.summary =
                     summary.summaryResponses?.find((s) => s.date === yesterday) ??
                     null;

                  this.errorStatus = this.summary
                     ? null
                     : this.aiSummaryStore.getErrorStatus();

                  this.loading = false;
               });
         });
   }

   private resetState(): void {
      this.summary = null;
      this.totalTaskNumber = 0;
      this.errorStatus = null;
      this.loading = false;
   }

   get financialRisk(): string {
      const total =
         this.summary?.modelSummaries
            .filter((m) => !m.hasError && m.isFinancial)
            .reduce((sum, m) => sum + m.amount, 0) ?? 0;

      return total.toFixed(2);
   }

   get operationsRisk(): number {
      return (
         this.summary?.modelSummaries.filter(
            (m) => !m.hasError && !m.isFinancial && !m.isAutoCompleted
         ).length ?? 0
      );
   }

   get totalTasks(): number {
      return this.totalTaskNumber;
   }

   get aiTasksRun(): number {
      return this.summary?.processed ?? 0;
   }

   get autoCompletedTasks(): number {
      return this.summary?.completed ?? 0;
   }

   goToTasksManagement(): void {
      this.router.navigate(['/workflow/tasks']);
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
